"use client"

import { LanguagePublish, VisibilityPublish } from "@/(admin)/modules/post/pages/StatusPublish"
import { describeDisplayWindow } from "@/(admin)/components/publish/display-window"
import { DisplayWindowMode, DisplayWindowModal } from "@/(admin)/components/publish/display-window-modal"
import { useCheckPermission } from "@/hook/use-check-permission"
import { t } from "@/languages/i18n"
import type { PostWorkflowStatus } from "@/types/types"
import { buildPostStatusDisplay, normalizePostStatus } from "@/utils/post-status-display"
import {
  CheckOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  ExclamationCircleFilled,
  SendOutlined,
  StopOutlined
} from "@ant-design/icons"
import { App, Button, Input, message, Space, Tag, Typography } from "antd"
import dayjs from "dayjs"
import React, { useEffect, useState } from "react"
import { PublishProps } from "../../../../lib/config/types"

const { Text } = Typography

interface LocalizationEntry {
  id: number
  locale: string
}

interface ExtendedPublishProps extends Omit<PublishProps, "status" | "setStatus"> {
  status: PostWorkflowStatus
  onPreview?: () => void
  onView?: () => void
  existingLanguages?: string[]
  localizations?: LocalizationEntry[]
  onNavigateToLocale?: (id: number) => void
  onCreateLocale?: (targetLocale: string) => void
  viOnly?: boolean
  isEditMode?: boolean
  approveLoading?: boolean
  rejectLoading?: boolean
  reviewActionLoading?: boolean
  onRequestReview?: () => void
  onApprove?: () => void
  onReject?: (note: string) => void
  publishDate?: string | null
  onUnpublish?: () => void
  onPublishNow?: (unpublishAt: string | null) => void
  onSchedulePublish?: (scheduledAt: string, unpublishAt: string | null) => void
  unpublishAt?: string | null
  onScheduleUnpublish?: (unpublishAt: string | null) => void
  onStatusChange?: (status: PostWorkflowStatus) => void
  reviewNote?: string | null
  submittedAt?: string | null
  reviewedAt?: string | null
}

const formatDateTime = (iso: string | null | undefined): string => {
  if (!iso) return ""
  return dayjs(iso).format("HH:mm DD/MM/YYYY")
}

const Publish: React.FC<ExtendedPublishProps> = ({
  status = "draft",
  language,
  setLanguages,
  onPreview,
  onView,
  existingLanguages = [],
  localizations = [],
  onNavigateToLocale,
  onCreateLocale,
  viOnly = false,
  isEditMode = false,
  approveLoading = false,
  rejectLoading = false,
  reviewActionLoading = false,
  onRequestReview,
  onApprove,
  onReject,
  publishDate = null,
  onPublishNow,
  onUnpublish,
  onSchedulePublish,
  unpublishAt = null,
  onScheduleUnpublish,
  onStatusChange,
  isCreateEdit,
  reviewNote,
  submittedAt,
  reviewedAt,
}) => {
  const [scheduledAt, setScheduledAt] = useState<string | null>(publishDate)
  const { modal } = App.useApp()
  const { hasPermission } = useCheckPermission()

  const normalizedStatus = normalizePostStatus(status, publishDate)

  const canRequestReview = hasPermission("post", "submit")
  const canApprove = hasPermission("post", "approve")
  const canReject = hasPermission("post", "reject")
  const canPublish = hasPermission("post", "publish")
  const canSchedule = hasPermission("post", "schedule")
  const canUnpublish = hasPermission("post", "unpublish")

  // Ai có quyền publish hoặc schedule thì được đăng/lên lịch thẳng, không cần
  // qua vòng duyệt. Dùng chính quyền post.publish / post.schedule làm ranh giới
  // (CTV không có 2 quyền này). Modal xuất bản vẫn tách nút Đăng ngay (canPublish)
  // và Lên lịch (canSchedule) nên quyền được kiểm đúng ở từng thao tác.
  const canPublishDirectly = canPublish || canSchedule
  const PUBLISHABLE: PostWorkflowStatus[] = ["draft", "rejected", "approved"]

  const resolvedApproveLoading = approveLoading || reviewActionLoading
  const resolvedRejectLoading = rejectLoading || reviewActionLoading
  const resolvedOtherLoading = reviewActionLoading

  const NON_REVIEWABLE: PostWorkflowStatus[] = ["pending", "approved", "published", "scheduled"]

  useEffect(() => {
    setScheduledAt(publishDate)
  }, [publishDate])

  const isLiveOrScheduled = normalizedStatus === "published" || normalizedStatus === "scheduled"

  const hasReviewActions = isEditMode && (
    (canRequestReview && !canPublishDirectly && !NON_REVIEWABLE.includes(normalizedStatus)) ||
    normalizedStatus === "pending" ||
    (canPublishDirectly && PUBLISHABLE.includes(normalizedStatus)) ||
    (isLiveOrScheduled && canUnpublish) ||
    (isLiveOrScheduled && canSchedule)
  )

  const statusItems = buildPostStatusDisplay({ status: normalizedStatus, scheduledAt: publishDate }, t)

  const hasReviewInfo =
    (normalizedStatus === "pending" && submittedAt) ||
    (normalizedStatus === "approved" && reviewedAt) ||
    (normalizedStatus === "rejected" && (reviewedAt || reviewNote)) ||
    (normalizedStatus === "scheduled" && publishDate) ||
    (normalizedStatus === "published" && unpublishAt)

  const [windowMode, setWindowMode] = useState<DisplayWindowMode | null>(null)
  const openWindowModal = (mode: DisplayWindowMode) => setWindowMode(mode)

  const handleOpenRejectModal = () => {
    let reviewNote = ""

    modal.confirm({
      title: t("Từ chối bài viết"),
      width: 500,
      icon: <CloseOutlined className="text-red-500" />,
      content: (
        <Space direction="vertical" size={16} className="w-full mt-4">
          <Text className="text-sm text-gray-600">
            {t("Vui lòng nhập lý do từ chối bài viết này.")}
          </Text>
          <Input.TextArea
            rows={4}
            placeholder={t("Nhập lý do từ chối...")}
            maxLength={500}
            showCount
            autoFocus
            onChange={(e) => {
              reviewNote = e.target.value
            }}
          />
        </Space>
      ),
      onOk: async () => {
        const trimmedNote = reviewNote.trim()

        if (!trimmedNote) {
          message.error(t("Vui lòng nhập lý do từ chối"))
          return Promise.reject()
        }

        await onReject?.(trimmedNote)
      },
      okText: t("Xác nhận từ chối"),
      okButtonProps: { danger: true, loading: resolvedRejectLoading },
      cancelText: t("Hủy"),
    })
  }

  const handleConfirmUnpublish = () => {
    const isScheduled = normalizedStatus === "scheduled"
    modal.confirm({
      title: isScheduled ? t("Xác nhận bỏ lịch đăng") : t("Xác nhận hủy xuất bản"),
      icon: <ExclamationCircleFilled />,
      content: isScheduled
        ? t("Bài viết sẽ quay về bản nháp, lịch đăng và lịch gỡ (nếu có) sẽ bị xóa.")
        : t("Bạn có chắc chắn muốn hủy xuất bản bài viết này? bài viết sẽ quay trở lại trạng thái bản nháp."),
      okText: t("Xác nhận"),
      okType: "danger",
      cancelText: t("Hủy"),
      onOk() {
        onUnpublish?.()
      },
    })
  }

  return (
    <Space direction="vertical" className="w-full" size={10}>
      <div className="flex flex-col gap-2">
        <div className="flex justify-between items-center border-b border-gray-100 pb-2">
          <Text className="text-sm font-medium" style={{ color: "var(--admin-foreground)" }}>
            {t("Trạng thái hiện tại")}
          </Text>
          <div className="flex gap-1">
            {statusItems.map((item) => (
              <Tag key={item.label} color={item.tone} className="mr-0">
                {item.label}
              </Tag>
            ))}
          </div>
        </div>
    
        {hasReviewInfo && (
          <div className="flex flex-col gap-1 bg-gray-50 rounded-md px-3 py-2 border border-gray-100 text-xs text-gray-500">
            {normalizedStatus === "pending" && submittedAt && (
              <span>
                {t("Đã yêu cầu kiểm duyệt lúc")}{" "}
                <strong className="text-gray-700">{formatDateTime(submittedAt)}</strong>
              </span>
            )}

            {normalizedStatus === "approved" && reviewedAt && (
              <span>
                {t("Đã duyệt lúc")}{" "}
                <strong className="text-green-700">{formatDateTime(reviewedAt)}</strong>
              </span>
            )}

            {normalizedStatus === "rejected" && (
              <>
                {reviewedAt && (
                  <span>
                    {t("Bị từ chối lúc")}{" "}
                    <strong className="text-red-600">{formatDateTime(reviewedAt)}</strong>
                  </span>
                )}
                {reviewNote && (
                  <span className="mt-0.5">
                    {t("Lí do bị từ chối")}:{" "}
                    <strong className="text-red-600">{reviewNote}</strong>
                  </span>
                )}
              </>
            )}

            {isLiveOrScheduled && (
              <span>
                {describeDisplayWindow({
                  start: normalizedStatus === "scheduled" ? publishDate : null,
                  end: unpublishAt,
                })}
              </span>
            )}
          </div>
        )}

        {hasReviewActions && (
          <div className="bg-gray-50 p-3 rounded-lg border border-gray-100 mt-2">
            <div className="flex flex-wrap gap-2">
              {canRequestReview && !canPublishDirectly && !NON_REVIEWABLE.includes(normalizedStatus) && (
                <Button
                  block
                  type="default"
                  icon={<SendOutlined />}
                  onClick={onRequestReview}
                  loading={resolvedOtherLoading}
                  className="flex items-center justify-center"
                >
                  {t("Yêu cầu duyệt bài")}
                </Button>
              )}

              {normalizedStatus === "pending" && (
                <div className="grid grid-cols-2 gap-2 w-full">
                  {canApprove && (
                    <Button
                      type="primary"
                      icon={<CheckOutlined />}
                      onClick={onApprove}
                      loading={resolvedApproveLoading}
                      className="flex items-center justify-center bg-green-600 hover:!bg-green-500 border-none"
                    >
                      {t("Duyệt bài")}
                    </Button>
                  )}
                  {canReject && (
                    <Button
                      danger
                      icon={<CloseOutlined />}
                      onClick={handleOpenRejectModal}
                      loading={resolvedRejectLoading}
                      className="flex items-center justify-center"
                    >
                      {t("Từ chối")}
                    </Button>
                  )}
                </div>
              )}

              {canPublishDirectly && PUBLISHABLE.includes(normalizedStatus) && (
                <Button
                  block
                  type="primary"
                  onClick={() => openWindowModal("publish")}
                  loading={resolvedOtherLoading}
                  className="flex items-center justify-center"
                >
                  {t("Xuất bản")}
                </Button>
              )}

              {normalizedStatus === "scheduled" && canSchedule && (
                <Button
                  block
                  icon={<ClockCircleOutlined />}
                  onClick={() => openWindowModal("edit")}
                  loading={resolvedOtherLoading}
                  className="flex items-center justify-center"
                >
                  {t("Sửa thời gian hiển thị")}
                </Button>
              )}

              {normalizedStatus === "published" && canSchedule && (
                <Button
                  block
                  icon={<ClockCircleOutlined />}
                  onClick={() => openWindowModal("end")}
                  loading={resolvedOtherLoading}
                  className="flex items-center justify-center"
                >
                  {unpublishAt ? t("Sửa lịch gỡ bài") : t("Lên lịch gỡ bài")}
                </Button>
              )}

              {isLiveOrScheduled && canUnpublish && (
                <Button
                  block
                  danger
                  icon={<StopOutlined />}
                  onClick={handleConfirmUnpublish}
                  loading={resolvedOtherLoading}
                  className="flex items-center justify-center"
                >
                  {normalizedStatus === "scheduled" ? t("Bỏ lịch đăng") : t("Hủy xuất bản")}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      <DisplayWindowModal
        mode={windowMode}
        onClose={() => setWindowMode(null)}
        current={{ start: normalizedStatus === "scheduled" ? publishDate : null, end: unpublishAt }}
        canPublish={canPublish}
        canSchedule={canSchedule}
        loading={resolvedOtherLoading}
        publishTitle={t("Xuất bản bài viết")}
        onPublishNow={end => onPublishNow?.(end)}
        onSchedule={(start, end) => onSchedulePublish?.(start, end)}
        onSaveEnd={end => onScheduleUnpublish?.(end)}
      />

      <LanguagePublish
        language={language}
        setLanguages={setLanguages}
        isCreateEdit={isCreateEdit}
        existingLanguages={existingLanguages}
        localizations={localizations}
        onNavigateToLocale={onNavigateToLocale}
        onCreateLocale={onCreateLocale}
        viOnly={viOnly}
      />

      <VisibilityPublish status={normalizedStatus === "published" ? "Publish" : "Draft"} onView={onView} />
    </Space>
  )
}

export default Publish